import { useState } from "react";
import { useLocation, Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, Building, ChevronDown, ChevronUp, ExternalLink, Factory, FileText, Globe, Handshake, Map, MapPin, Pencil, Star, Tag, Trash2, UserPlus, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { useAuth, useAuthFeatures } from "@/context/AuthContext";
import { PersonPicker } from "@/components/persons/PersonPicker";
import { OrgLogo, PersonAvatar } from "@/components/common/Visuals";
import { ConfirmDialog, OrgStatusBadges } from "@/components/common/Dialogs";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { InfoRow } from "@/components/common/Layout";
import { BackLink, DetailCard, HistorySection } from "@/components/common/Detail";
import { plural } from "@/lib/format";
import { cn } from "@/lib/utils";

function ContactsSection({ org }) {
  const { isAuthed } = useAuth();
  const features = useAuthFeatures();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const contacts = useQuery({
    queryKey: ["org-contacts", org.id, isAuthed],
    queryFn: async () => (await api.get(`/organizations/${org.id}/contacts`)).data,
    enabled: open,
  });
  const refresh = () => ["org-contacts", "organization", "persons", "person"].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  const link = async (person, action) => {
    try {
      await api.post(`/organizations/${org.id}/contacts`, { person_id: person.id, action });
      toast.success(action === "add" ? `${person.full_name} rattaché(e)` : `${person.full_name} détaché(e) (fiche conservée)`);
      refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const setMain = async (person) => {
    try {
      await api.patch(`/organizations/${org.id}`, { main_contact_ids: [person.id] });
      toast.success("Contact principal mis à jour");
      refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <section className="lm-card mt-6 p-6" data-testid="org-contacts-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-bold" data-testid="org-contacts-count">
          <Users className="h-5 w-5 text-primary" /> Contacts — {plural(org.contacts_count, "personne", "personnes")}
        </h2>
        <div className="flex flex-wrap gap-2">
          {isAuthed && (
            <>
              <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="org-add-contact-button">
                <Link to={`/personnes/nouveau?org=${org.id}`}><UserPlus className="h-4 w-4" /> Créer une personne</Link>
              </Button>
              <PersonPicker label="Rattacher une personne existante" onPick={(p) => link(p, "add")} excludeIds={contacts.data?.items.map((p) => p.id) || []} testId="org-attach-person" />
            </>
          )}
          {org.contacts_count > 0 && (
            <Button size="sm" className="rounded-full" onClick={() => setOpen((v) => !v)} data-testid="org-toggle-contacts-button">
              {open ? <><ChevronUp className="h-4 w-4" /> Masquer</> : <><ChevronDown className="h-4 w-4" /> Voir les contacts</>}
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-5 lm-fade-up">
          {contacts.isLoading && <LoadingGrid count={3} />}
          {contacts.isError && <ErrorState onRetry={contacts.refetch} />}
          {contacts.data && (
            <>
              <div className="grid gap-3 md:grid-cols-2" data-testid="org-contacts-list">
                {contacts.data.items.map((p) => (
                  <div key={p.id} data-testid={`org-contact-${p.id}`}
                    className={cn("flex items-center gap-3 rounded-2xl border p-3.5 transition-colors",
                      p.is_main ? "border-amber-300 bg-amber-50/70" : "border-border hover:border-primary/30 hover:bg-accent/30")}>
                    <PersonAvatar person={p} size="sm" className="rounded-xl" />
                    <Link to={`/personnes/${p.id}`} className="min-w-0 flex-1">
                      <div className="truncate font-semibold hover:underline">{p.full_name}</div>
                      <div className="truncate text-sm text-muted-foreground">{p.job_title}</div>
                    </Link>
                    {p.is_main && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-400/90 px-2.5 py-0.5 text-[11px] font-bold text-amber-950" data-testid="main-contact-badge">
                        <Star className="h-3 w-3 fill-current" /> Principal
                      </span>
                    )}
                    {isAuthed && features.org_main_contact && !p.is_main && (
                      <button type="button" onClick={() => setMain(p)} title="Désigner contact principal" className="rounded-full p-1.5 text-amber-600 hover:bg-amber-50" data-testid={`org-set-main-${p.id}`}><Star className="h-4 w-4" /></button>
                    )}
                    {isAuthed && (
                      <button type="button" onClick={() => link(p, "remove")} title="Détacher (la fiche n'est pas supprimée)" className="rounded-full p-1.5 text-red-600 hover:bg-red-50" data-testid={`org-detach-${p.id}`}><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              {isAuthed && !features.org_main_contact && <p className="mt-3 text-xs text-muted-foreground">« Contact principal » : propriété absente de Maisonna, désignation indisponible.</p>}
              <Link to={`/personnes?organization=${org.id}`} className="mt-4 inline-block text-sm font-bold text-primary hover:underline" data-testid="org-contacts-directory-link">
                Ouvrir ces contacts dans le répertoire (filtres, sélection, export) →
              </Link>
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function OrgDetailPage() {
  const { id } = useParams();
  const { isAuthed, isAdmin } = useAuth();
  const features = useAuthFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [untenant, setUntenant] = useState(false);
  const tenantMode = location.pathname.startsWith("/locataires");
  const base = tenantMode ? "/locataires" : "/entreprises";
  const { data: o, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["organization", id, isAuthed],
    queryFn: async () => (await api.get(`/organizations/${id}`)).data,
  });

  if (isLoading) return <div className="lm-container py-10"><LoadingGrid count={3} /></div>;
  if (isError) {
    return <div className="lm-container py-10">{error.response?.status === 404
      ? <EmptyState title="Organisation introuvable" action={<Button asChild className="rounded-full"><Link to={base}>{tenantMode ? "Retour aux locataires" : "Retour aux entreprises"}</Link></Button>} />
      : <ErrorState onRetry={refetch} />}</div>;
  }

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/organizations/${id}`);
      toast.success("Organisation supprimée (archivée dans Notion)");
      navigate(base);
      queryClient.removeQueries({ queryKey: ["organization", id] });
      queryClient.invalidateQueries();
    } catch (err) {
      toast.error(errorMessage(err));
      setDeleting(false);
    }
  };

  const removeTenant = async () => {
    setDeleting(true);
    try {
      await api.post(`/organizations/${id}/tenant`, { action: "remove" });
      toast.success("Statut Locataire retiré (fiche et autres statuts conservés)");
      queryClient.invalidateQueries();
      setUntenant(false);
      if (tenantMode) navigate(`/entreprises/${id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="lm-container pb-20">
      <BackLink to={base} label={tenantMode ? "Locataires" : "Entreprises"} />
      <section className="lm-card relative overflow-hidden p-6 sm:p-8 lm-fade-up" data-testid="org-detail-header">
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-r from-emerald-500/10 via-[#EC7F13]/5 to-[#A4247F]/10" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end">
          <OrgLogo org={o} size="xl" className="ring-4 ring-white" />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-extrabold sm:text-4xl" data-testid="org-detail-name">{o.name}</h1>
            <p className="mt-1 text-lg text-stone-600">{[...o.org_type, ...o.sector].filter(Boolean).join(" · ")}</p>
            <div className="mt-3"><OrgStatusBadges org={o} /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {o.website && <Button asChild variant="outline" className="rounded-full" data-testid="org-website-button"><a href={o.website} target="_blank" rel="noreferrer"><Globe className="h-4 w-4" /> Site web</a></Button>}
            {isAuthed && (
              <>
                <Button asChild className="rounded-full" data-testid="org-edit-button"><Link to={`${base}/${id}/modifier`}><Pencil className="h-4 w-4" /> Modifier</Link></Button>
                {o.notion_url && <Button asChild variant="outline" className="rounded-full" data-testid="org-open-notion-button"><a href={o.notion_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Ouvrir dans Notion</a></Button>}
              </>
            )}
            {isAuthed && o.is_tenant && (
              <Button variant="outline" className="rounded-full" onClick={() => setUntenant(true)} data-testid="org-remove-tenant-button">
                <Building className="h-4 w-4" /> Retirer des locataires
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirm(true)} data-testid="org-delete-button">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <DetailCard title="Coordonnées" icon={MapPin} testId="org-info-card">
          <InfoRow icon={Globe} label="Site internet">{o.website && <a href={o.website} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">{o.website.replace(/^https?:\/\//, "")}</a>}</InfoRow>
          <InfoRow icon={MapPin} label="Adresse">{o.address}</InfoRow>
          <InfoRow icon={Map} label="Département">{o.department.join(", ")}</InfoRow>
          {isAuthed && <InfoRow icon={FileText} label="SIRET">{o.siret}</InfoRow>}
          {o.description && <p className="pt-3 text-sm text-stone-700">{o.description}</p>}
        </DetailCard>
        <DetailCard title="Profil" icon={Tag} testId="org-profile-card">
          <InfoRow icon={Briefcase} label="Type d'organisation">{o.org_type.join(", ")}</InfoRow>
          <InfoRow icon={Factory} label="Secteur d'activité">{o.sector.join(", ")}</InfoRow>
          {isAuthed && <InfoRow icon={Handshake} label="Relation La Mêlée">{o.relation_types?.join(", ")}</InfoRow>}
          <div className="py-2.5" data-testid="org-expertises">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">Expertises</div>
            {features.org_expertises ? (o.expertises.length ? <div className="flex flex-wrap gap-2">{o.expertises.map((e) => <span key={e} className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">{e}</span>)}</div> : <span className="text-sm text-muted-foreground">Aucune</span>)
              : <span className="text-xs text-muted-foreground">Propriété « Expertises » absente de Maisonna.</span>}
          </div>
        </DetailCard>
      </div>
      <ContactsSection org={o} />
      {isAuthed && o.history && (
        <HistorySection sections={[
          { label: "Adhésion 2026", items: o.history.membership_2026.map((t) => ({ title: t })) },
          { label: "Pack annuel", items: o.history.pack ? [{ title: o.history.pack }] : [] },
        ]} />
      )}
      <ConfirmDialog open={untenant} onOpenChange={setUntenant} loading={deleting} onConfirm={removeTenant} confirmLabel="Retirer" title={`Retirer ${o.name} des locataires ?`}
        description="Seule la valeur « Locataire » est retirée de la propriété Type dans Maisonna. La fiche, ses autres statuts, contacts et relations sont conservés." />
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} loading={deleting} onConfirm={remove} title={`Supprimer ${o.name} ?`}
        description="L'organisation sera archivée dans Notion (corbeille) et disparaîtra de l'annuaire. Les contacts rattachés ne sont pas supprimés." />
    </div>
  );
}
